import React from "react";
import PropTypes from "prop-types";
import {
  Card,
  CardContent,
  CardActions,
  Box,
  makeStyles,
  Typography,
  IconButton,
  Chip,
  Avatar,
} from "@material-ui/core";
import { fade } from "@material-ui/core/styles";
import { useTranslate } from "react-polyglot";
import CloseIcon from "@material-ui/icons/Close";
import oneCoin from "../../assets/ic_one_coin.png";
import timer from "../../assets/ic_timer.svg";
import shoppingCart from "../../assets/ic_shopping-cart-2.svg";
import checkCircle from "../../assets/ic_check-circle_3.svg";
import { useBaseData } from "../../Providers/Data/BaseDataProvider.jsx";
import FlatPrimaryButton from "../../SharedComponents/FlatPrimaryButton.jsx";
import { statusProductOffer } from "../../constants.js";
import { isOneTimeOfferPurchased } from "../../Utils/misc.js";

const useStyles = makeStyles((theme) => ({
  cardRoot: {
    display: "flex",
    flexDirection: "column",
    minHeight: 350,
    alignItems: "center",
    paddingTop: 16,
  },
  content: {
    flex: "1 0 auto",
    padding: "12px 20px",
    maxHeight: "70vh",
    overflowY: "auto",
    width: "100%",
    borderTop: "2px solid rgba(0, 0, 0, .08)",
  },
  contentBox: {
    "& p *": {
      overflowWrap: "anywhere",
    },
    overflowWrap: "anywhere",
    padding: "24px 0",
  },
  action: {
    padding: "18px 24px",
    marginLeft: "auto",
    marginRight: "auto",
  },
  closeButton: {
    position: "absolute",
    top: 8,
    right: 8,
  },
  imageBox: {
    width: "25vw",
    height: "auto",
    overflow: "hidden",
  },
  offerImg: {
    width: "100%",
    height: "100%",
    display: "block",
    objectFit: "cover",
    objectPosition: "center",
  },
  title: {
    color: "#1B1C1C",
    fontSize: 22,
    fontWeight: 500,
  },
  priceBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FCF3DE",
    width: "max-content",
    padding: theme.spacing(1),
    borderRadius: theme.spacing(1),
  },

  oneCoin: {
    width: 20,
    height: 20,
  },

  contentText: {
    color: "#49454F",
    fontSize: 18,
    marginTop: 12,
  },
  coinPrice: {
    color: theme.palette.secondary.yellow,
    marginRight: theme.spacing(0.75),
    fontSize: 18,
    lineHeight: 1,
  },
  orderButtonBottom: {
    minWidth: 200,
    textTransform: "none",
    borderRadius: 22,
  },
  buyIcon: {
    width: 28,
    height: 28,
    marginRight: theme.spacing(1),
  },
  chipApproved: {
    position: "absolute",
    top: theme.spacing(2),
    left: theme.spacing(4),
    color: "#226C36",
    backgroundColor: "#fff",
    "&::before": {
      content: '""',
      position: "absolute",
      inset: 0,
      background: fade("#226C36", 0.12),
      borderRadius: 16,
    },
  },
}));

/**
 * @param {ProductOffer} offer
 * @param {Function}setStep
 * @param {?Function} onClose
 * @returns {*}
 * @constructor
 */
const ProductDetails = ({ offer, setStep, onClose }) => {
  const classes = useStyles();
  const t = useTranslate();
  const { coinBalance } = useBaseData();

  if (!offer) {
    return null;
  }

  /** */
  const onCloseCard = () => {
    onClose && onClose();
  };
  /** */
  const onNextStep = () => {
    setStep && setStep("order");
  };

  return (
    <Card className={classes.cardRoot}>
      {(offer.status === statusProductOffer.approved || isOneTimeOfferPurchased(offer)) && (
        <Chip
          className={classes.chipApproved}
          avatar={<Avatar alt="checkCircle" src={checkCircle} />}
          label={t("productCatalog.purchased")}
        />
      )}
      <Box className={classes.imageBox}>
        <img className={classes.offerImg} src={offer.image.url} alt={offer.image.name} />
      </Box>
      <CardContent className={classes.content}>
        <Typography className={classes.title} variant="h6" gutterBottom>
          {offer.name}
        </Typography>
        <Box className={classes.priceBox}>
          <Typography className={classes.coinPrice}>{offer.price}</Typography>
          <img className={classes.oneCoin} src={oneCoin} alt="oneCoin" />
        </Box>
        <Typography className={classes.contentText}>
          <span dangerouslySetInnerHTML={offer.description.sanitizedData()} />
        </Typography>
      </CardContent>
      <CardActions className={classes.action}>
        {isOneTimeOfferPurchased(offer) ? (
          // Уже купленный одноразовый товар: блокируем кнопку покупки
          <FlatPrimaryButton disabled className={classes.orderButtonBottom}>
            <img className={classes.buyIcon} src={checkCircle} alt="checkCircle" />
            {t("productCatalog.purchased")}
          </FlatPrimaryButton>
        ) : (
          coinBalance >= offer.price && (
            <FlatPrimaryButton
              disabled={offer.status === statusProductOffer.pending}
              className={classes.orderButtonBottom}
              onClick={onNextStep}
            >
              {offer.status === statusProductOffer.pending ? (
                <>
                  <img className={classes.buyIcon} src={timer} alt="timer" />
                  {t("productCatalog.inProcessing")}
                </>
              ) : (
                <>
                  <img className={classes.buyIcon} src={shoppingCart} alt="shoppingCart" />
                  {t("productCatalog.order")}
                </>
              )}
            </FlatPrimaryButton>
          )
        )}
      </CardActions>
      <IconButton aria-label="close" className={classes.closeButton} onClick={onCloseCard}>
        <CloseIcon />
      </IconButton>
    </Card>
  );
};
ProductDetails.propTypes = {
  offer: PropTypes.object.isRequired,
  setStep: PropTypes.func,
  onClose: PropTypes.func,
};

export default ProductDetails;
